import { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { authenticateWebview } from 'entities/auth/api/authApi'
import type { ApiError } from 'shared/api/createHttpClient'
import { getErrorMessage } from 'shared/lib/getErrorMessage'
import {
  clearAuthTokens,
  clearMpulseToken,
  finishAuthBootstrap,
  getAuthUser,
  getMpulseToken,
  hasAnyAuthToken,
  setAuthTokensFromBackend,
  setAuthUser,
  setMpulseToken,
  startAuthBootstrap,
} from 'shared/lib/auth'

function isBadJsTokenError(error: unknown): error is ApiError {
  if (!error || typeof error !== 'object') return false
  if (!('status' in error)) return false
  const status = (error as { status?: unknown }).status
  return status === 400 || status === 401
}

type WebviewBootstrapResult =
  | { ok: true }
  | { ok: false; redirectToAuth: true }
  | { ok: false; error: string }

export async function bootstrapWebviewAuth(
  jsChannelToken: string | null = null
): Promise<WebviewBootstrapResult> {
  startAuthBootstrap()

  try {
    const persistedMpulseToken = getMpulseToken()?.trim() || null
    const shouldUseMpulseBootstrap = Boolean(
      jsChannelToken || (persistedMpulseToken && !hasAnyAuthToken())
    )
    const mpulseCandidates = [
      { token: jsChannelToken, source: 'js' as const },
      { token: persistedMpulseToken, source: 'storage' as const },
    ].filter(
      (candidate, index, arr): candidate is { token: string; source: 'js' | 'storage' } =>
        Boolean(candidate.token) &&
        arr.findIndex((item) => item.token === candidate.token) === index
    )

    if (shouldUseMpulseBootstrap && mpulseCandidates.length > 0) {
      let lastError: unknown = null

      for (const candidate of mpulseCandidates) {
        const candidateToken = candidate.token

        try {
          const response = await authenticateWebview({ access_token: candidateToken })
          setAuthTokensFromBackend(response.auth)
          setAuthUser(response.user)
          setMpulseToken(candidateToken)

          return { ok: true }
        } catch (err) {
          if (candidate.source === 'js' && isBadJsTokenError(err)) {
            lastError = err
            continue
          }
          lastError = err
          break
        }
      }

      throw lastError ?? new Error('Не удалось выполнить авторизацию webview')
    }

    const hasStoredTokens = hasAnyAuthToken()
    const storedUser = getAuthUser()

    if (!hasStoredTokens) {
      return { ok: false, redirectToAuth: true }
    }

    if (storedUser) {
      return { ok: true }
    }

    return { ok: true }
  } catch (err) {
    clearAuthTokens()
    clearMpulseToken()
    return { ok: false, error: getErrorMessage(err) }
  } finally {
    finishAuthBootstrap()
  }
}

export function useWebviewAuthBootstrap() {
  const navigate = useNavigate()
  const [isAuthBootstrapping, setIsAuthBootstrapping] = useState(true)
  const [authBootstrapError, setAuthBootstrapError] = useState<string | null>(null)

  useEffect(() => {
    let isCancelled = false

    const bootstrapAuth = async () => {
      const result = await bootstrapWebviewAuth(null)
      if (isCancelled) return

      if (!result.ok && 'redirectToAuth' in result && result.redirectToAuth) {
        await navigate({ to: '/auth' })
        return
      }

      if (!result.ok && 'error' in result) {
        setAuthBootstrapError(result.error)
        setIsAuthBootstrapping(false)
        return
      }

      setAuthBootstrapError(null)
      setIsAuthBootstrapping(false)
    }

    void bootstrapAuth()

    return () => {
      isCancelled = true
    }
  }, [navigate])

  return {
    authBootstrapError,
    isAuthBootstrapping,
  }
}
